import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, of } from 'rxjs';
import { map, take, filter, timeout, catchError } from 'rxjs/operators';
import { LoginResponse, OidcSecurityService } from 'angular-auth-oidc-client';
import { AutoLogoutService } from '../modules/shared/services/auto-logout.service';
import { IframeAuthService } from '../modules/core/services/iframe-auth.service';
import { AuthService } from '../modules/core/services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class MemberGuard  {

  constructor(
    public router: Router,
    private oidcSecurityService: OidcSecurityService,
    private autoLogoutService: AutoLogoutService,
    private iframeAuthService: IframeAuthService,
    private authService: AuthService
  ) { }

  canActivate(): Observable<boolean> | boolean {
    // Check if running in iframe mode
    if (this.authService.isIframeMode) {
      return this.checkIframeAuth();
    }

    // Standalone mode - use OIDC
    return this.oidcSecurityService.checkAuth()
      .pipe(
        take(1),
        map((response: LoginResponse) => {
          if (response.isAuthenticated) {
            this.autoLogoutService.setIdleConfig();
            this.autoLogoutService.setUserLoggedIn(true);
            return true;
          }
          // url doesn't have a hash part
          if (!window.location.hash) {
            this.router.navigate(['/login']);
          }
          return false;
        })
      );
  }

  private checkIframeAuth(): Observable<boolean> {
    const isAuthenticated = this.iframeAuthService.isAuthenticated;

    if (isAuthenticated) {
      this.autoLogoutService.setIdleConfig();
      this.autoLogoutService.setUserLoggedIn(true);
      return of(true);
    }

    // In iframe mode, if not authenticated, wait for token from parent window
    // This handles the case where the iframe loads before receiving the first token
    console.log('[MemberGuard] Waiting for iframe authentication token...');
    return this.iframeAuthService.tokenReceived$
      .pipe(
        filter(received => received === true),  // Skip initial false value, only proceed when token arrives
        take(1),  // Take the first true value
        timeout(5000),  // Fail after 5 seconds if token never arrives
        map(() => {
          if (this.iframeAuthService.isAuthenticated) {
            this.autoLogoutService.setIdleConfig();
            this.autoLogoutService.setUserLoggedIn(true);
            console.log('[MemberGuard] Iframe authentication successful');
            return true;
          }
          console.warn('[MemberGuard] Token received but authentication failed');
          return false;
        }),
        catchError((err) => {
          console.error('[MemberGuard] Timeout waiting for iframe authentication token', err);
          return of(false);
        })
      );
  }
}

